import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// Credit Cards Payoff Calculator (Avalanche) — up to 6 cards
export default function CreditCardsPayoffCalc() {
  const [budget, setBudget] = useState(500);
  const [cards, setCards] = useState([
    { name: 'Card 1', balance: 4600, min: 100, rate: 18.99 },
    { name: 'Card 2', balance: 3900, min: 90, rate: 19.99 },
    { name: 'Card 3', balance: 6000, min: 120, rate: 15.99 },
  ]);
  const [showMore, setShowMore] = useState(false);

  const updateCard = (i, patch) => setCards(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const addCard = () => setCards(prev => [...prev, { name: `Card ${prev.length+1}`, balance: 0, min: 0, rate: 0 }]);

  const c = useMemo(() => {
    const active = cards.filter(x => x.balance > 0).map((x, i) => ({ ...x, idx: i, monthsToPay: 0, totalInt: 0, totalPaid: 0, schedule: [] }));
    const totalMin = active.reduce((a,b)=>a+b.min, 0);
    if (budget < totalMin) return { months: 0, totalInt: 0, error: 'Budget below total minimums', rows: [], perCard: [] };
    let month = 0; let totalInt = 0; let totalPaid = 0;
    const rows = [];
    // Track the running "current payment amount" per card so we can emit "pay $X until month #Y" segments
    const lastPay = active.map(() => null);
    while (active.some(x => x.balance > 0.01) && month < 600) {
      month++;
      const alive = active.filter(x => x.balance > 0);
      const minSum = alive.reduce((a,b)=>a+b.min, 0);
      let extra = budget - minSum;
      const sortedIdx = [...active.keys()].filter(i => active[i].balance > 0).sort((a,b) => active[b].rate - active[a].rate);
      let monthInt = 0;
      for (const i of sortedIdx) {
        const card = active[i];
        const int = card.balance * card.rate/100/12;
        monthInt += int;
        totalInt += int;
        card.totalInt += int;
        let pay = card.min;
        if (i === sortedIdx[0]) pay += extra;
        if (pay > card.balance + int) {
          const excess = pay - (card.balance + int);
          pay -= excess;
          extra += excess;
        }
        card.balance = Math.max(0, card.balance + int - pay);
        card.totalPaid += pay;
        totalPaid += pay;
        // Record payment segment
        const paymentStr = pay.toFixed(2);
        if (lastPay[i] !== paymentStr) {
          card.schedule.push({ startMonth: month, endMonth: month, amount: pay });
          lastPay[i] = paymentStr;
        } else {
          card.schedule[card.schedule.length - 1].endMonth = month;
        }
        if (card.balance <= 0.01 && card.monthsToPay === 0) card.monthsToPay = month;
      }
      if (month <= 60) rows.push({ month, totalBal: active.reduce((a,b)=>a+b.balance,0), interest: monthInt });
    }
    return { months: month, totalInt, totalPaid, rows, perCard: active, error: null };
  }, [budget, cards]);

  // Convert schedule segments into human-readable "pay $X until month #Y. then pay $Z at month #Y+1 to pay off."
  const fmtSchedule = (card) => {
    if (!card.schedule || card.schedule.length === 0) return '—';
    const parts = card.schedule.map((seg, i) => {
      const last = i === card.schedule.length - 1;
      if (seg.startMonth === seg.endMonth) {
        return last ? `pay $${seg.amount.toFixed(2)} at month #${seg.endMonth} to pay off.` : `then pay $${seg.amount.toFixed(2)} at month #${seg.endMonth}.`;
      }
      return `${i === 0 ? 'pay' : 'then pay'} $${seg.amount.toFixed(2)} until month #${seg.endMonth - 1}.`;
    });
    return parts.join(' ');
  };

  return (
    <CalcShell look="cnet" title="Credit Cards Payoff Calculator" subtitle="Debt Avalanche payoff planner — pay minimums plus extra to the highest-rate card first."
      about={<><p>The <b>Debt Avalanche</b> method minimizes total interest paid by targeting the highest-rate debt with extra payments while paying minimums on all others.</p></>}
    >
      <div className="field mb-4"><label>Monthly budget set aside for credit cards</label><div className="flex gap-2 max-w-xs"><span className="self-center text-slate-500">$</span><input type="number" value={budget} onChange={e=>setBudget(+e.target.value||0)} className="flex-1"/></div></div>
      <h3 className="font-semibold text-slate-900 mb-2">Info of your credit cards:</h3>
      <table className="w-full text-sm">
        <thead className="bg-slate-100"><tr><th className="px-2 py-2 text-left">Credit card</th><th className="px-2 py-2 text-right">Balance</th><th className="px-2 py-2 text-right">Minimum payment</th><th className="px-2 py-2 text-right">Interest rate</th></tr></thead>
        <tbody>
          {cards.map((card, i) => (
            <tr key={i} className="border-b">
              <td className="px-2 py-2"><input type="text" value={card.name} onChange={e=>updateCard(i, { name: e.target.value })} className="!py-1"/></td>
              <td className="px-2 py-2"><div className="flex gap-1"><span>$</span><input type="number" value={card.balance} onChange={e=>updateCard(i, { balance: +e.target.value||0 })} className="!py-1 flex-1"/></div></td>
              <td className="px-2 py-2"><div className="flex gap-1"><span>$</span><input type="number" value={card.min} onChange={e=>updateCard(i, { min: +e.target.value||0 })} className="!py-1 flex-1"/></div></td>
              <td className="px-2 py-2"><div className="flex gap-1"><input type="number" step="0.01" value={card.rate} onChange={e=>updateCard(i, { rate: +e.target.value||0 })} className="!py-1 flex-1"/><span>%</span></div></td>
            </tr>
          ))}
        </tbody>
      </table>
      {cards.length < 6 && <button onClick={addCard} className="text-blue-700 text-sm font-semibold mt-2">+ Show more input fields</button>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="result-panel"><div className="result-title">Payoff time</div><div className="result-value" data-testid="ccp-time" style={{fontSize:'1.5rem'}}>{c.months ? `${Math.floor(c.months/12)} yr ${c.months%12} mo` : '—'}</div></div>
        <div className="result-panel"><div className="result-title">Total interest paid</div><div className="result-value" style={{fontSize:'1.5rem'}}>{fmt(c.totalInt)}</div></div>
        <div className="result-panel"><div className="result-title">Total paid</div><div className="result-value" style={{fontSize:'1.5rem'}}>{fmt(cards.reduce((a,b)=>a+b.balance,0) + c.totalInt)}</div></div>
      </div>
      {c.error && <div className="mt-3 p-3 bg-red-50 text-red-700 rounded text-sm">{c.error}</div>}

      {/* Per-card payoff details table — matches Calculator.net exactly */}
      {c.perCard && c.perCard.length > 0 && (
        <div className="mt-6">
          <p className="text-sm text-slate-700 mb-3">You can pay off your credit cards in <b className="text-green-700">{c.months} months ({Math.floor(c.months/12)} years and {c.months%12} months)</b> if you payback <b>${budget.toFixed(2)}</b> every month. To pay off, you will need to pay a total of <b>${(c.totalPaid).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</b>, within which interest is <b>${c.totalInt.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</b>.</p>
          <p className="text-sm text-slate-700 mb-3">The best way to pay off your credit cards is to pay back the high interest card first while paying the minimum payments for the other cards. The following are the payback schedules. This schedule assume you do not put new balance to the credit cards.</p>
          <div className="overflow-auto border border-slate-200 rounded">
            <table className="w-full text-sm border-collapse" data-testid="ccp-details-table">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="px-3 py-2 text-left">Credit Card</th>
                  <th className="px-3 py-2 text-left">Payoff Length</th>
                  <th className="px-3 py-2 text-right">Total Interest</th>
                  <th className="px-3 py-2 text-right">Total Payments</th>
                  <th className="px-3 py-2 text-left">Payment Schedule</th>
                </tr>
              </thead>
              <tbody>
                {[...c.perCard].sort((a,b) => a.monthsToPay - b.monthsToPay).map((card, i) => (
                  <tr key={i} className={`border-t border-slate-200 ${i%2?'bg-slate-50':''}`}>
                    <td className="px-3 py-2 whitespace-nowrap">#{card.idx+1}: {card.name}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{card.monthsToPay} months ({Math.floor(card.monthsToPay/12)} year{Math.floor(card.monthsToPay/12)!==1?'s':''} and {card.monthsToPay%12} months)</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{fmt(card.totalInt)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{fmt(card.totalPaid)}</td>
                    <td className="px-3 py-2 text-xs leading-relaxed">{fmtSchedule(card)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Balance-over-time chart */}
      {c.rows.length > 1 && (
        <div className="calc-card p-5 mt-6">
          <h3 className="font-bold text-slate-900 mb-3">Total Balance Over Time</h3>
          {(() => {
            const w = 640, h = 240, padL = 60, padR = 20, padT = 15, padB = 32;
            const max = Math.max(...c.rows.map(r => r.totalBal));
            const nMax = c.rows[c.rows.length-1].month;
            const xs = m => padL + (m / (nMax || 1)) * (w - padL - padR);
            const ys = v => h - padB - (v / (max || 1)) * (h - padT - padB);
            const balLine = c.rows.map(r => `${xs(r.month)},${ys(r.totalBal)}`).join(' ');
            return (
              <div className="overflow-auto">
                <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[500px]">
                  {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
                    <g key={i}>
                      <line x1={padL} y1={ys(t*max)} x2={w - padR} y2={ys(t*max)} stroke="#e2e8f0" strokeDasharray="3 3"/>
                      <text x={padL - 6} y={ys(t*max) + 4} textAnchor="end" fontSize="10" fill="#64748b">${Math.round(t*max/1000)}K</text>
                    </g>
                  ))}
                  <polyline points={balLine} fill="none" stroke="#dc2626" strokeWidth="2.5"/>
                  <polygon points={`${padL},${h-padB} ${balLine} ${xs(nMax)},${h-padB}`} fill="#dc2626" opacity="0.15"/>
                  <text x={padL} y={h - padB + 14} fontSize="10" fill="#64748b">M0</text>
                  <text x={xs(nMax) - 20} y={h - padB + 14} fontSize="10" fill="#64748b">M{nMax}</text>
                </svg>
                <div className="flex justify-center mt-2 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{background:'#dc2626'}}/>Total remaining balance</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </CalcShell>
  );
}
